import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "desktopMenu",
    "dropdown",
    "mobileOverlay",
    "mobilePanel",
    "hamburger"
  ]

  connect() {
    this.activeDropdown = null
    this.activeMobileDepth = 0
    this.hoverTimeout = null
    this.leaveTimeout = null

    this._onKeydown = this._onKeydown.bind(this)
    this._onResize = this._onResize.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("resize", this._onResize)
    this._clearTimeouts()
  }

  // Desktop: show dropdown on mouseenter of a top-level nav item
  showDropdown(event) {
    if (this._isMobile()) return
    this._clearTimeouts()

    const trigger = event.currentTarget
    const dropdownId = trigger.dataset.megaNavDropdown
    const dropdown = this.dropdownTargets.find(d => d.dataset.megaNavId === dropdownId)
    if (!dropdown) return

    this.hoverTimeout = setTimeout(() => {
      this._closeAllDropdowns()
      this._openDropdown(trigger, dropdown)
    }, 80)
  }

  // Desktop: hide dropdown on mouseleave with a delay
  scheduleClose(event) {
    if (this._isMobile()) return
    this._clearTimeouts()

    this.leaveTimeout = setTimeout(() => {
      this._closeAllDropdowns()
    }, 200)
  }

  // Desktop: cancel close when re-entering the dropdown area
  cancelClose() {
    if (this._isMobile()) return
    clearTimeout(this.leaveTimeout)
    this.leaveTimeout = null
  }

  // Desktop: show nested submenu
  showSubmenu(event) {
    if (this._isMobile()) return
    const item = event.currentTarget
    const submenu = item.querySelector(".mn-submenu")
    if (!submenu) return

    const siblings = item.parentElement.querySelectorAll(":scope > .mn-has-children")
    siblings.forEach(s => {
      if (s !== item) {
        s.classList.remove("mn-submenu-open")
        const sub = s.querySelector(".mn-submenu")
        if (sub) sub.classList.remove("mn-submenu--visible")
      }
    })

    item.classList.add("mn-submenu-open")
    submenu.classList.add("mn-submenu--visible")
    this._clampSubmenu(submenu)
  }

  hideSubmenu(event) {
    if (this._isMobile()) return
    const item = event.currentTarget
    const submenu = item.querySelector(".mn-submenu")
    if (!submenu) return
    item.classList.remove("mn-submenu-open")
    submenu.classList.remove("mn-submenu--visible")
  }

  // Mobile: toggle hamburger overlay
  toggleMobile() {
    if (this.mobileOverlayTarget.classList.contains("mn-mobile--open")) {
      this._closeMobile()
    } else {
      this._openMobile()
    }
  }

  // Mobile: drill into a submenu
  drillDown(event) {
    event.preventDefault()
    const panelId = event.currentTarget.dataset.megaNavPanel
    const panel = this.mobilePanelTargets.find(p => p.dataset.megaNavPanelId === panelId)
    if (!panel) return

    panel.classList.add("mn-mpanel--active")
    this.activeMobileDepth++
  }

  // Mobile: go back one level
  drillUp(event) {
    event.preventDefault()
    const panel = event.currentTarget.closest(".mn-mpanel")
    if (!panel) return

    panel.classList.remove("mn-mpanel--active")
    this.activeMobileDepth = Math.max(0, this.activeMobileDepth - 1)
  }

  // Private

  _openDropdown(trigger, dropdown) {
    trigger.classList.add("mn-nav-item--active")
    trigger.setAttribute("aria-expanded", "true")
    dropdown.classList.add("mn-dropdown--open")
    this._clampDropdown(dropdown)
    this.activeDropdown = { trigger, dropdown }
  }

  _clampSubmenu(submenu) {
    submenu.style.left = ""
    submenu.style.right = ""
    const rect = submenu.getBoundingClientRect()
    const vw = window.innerWidth
    if (rect.right > vw - 16) {
      submenu.style.left = "auto"
      submenu.style.right = "calc(100% + 4px)"
    }
  }

  _clampDropdown(dropdown) {
    dropdown.style.transform = "translateX(-50%)"
    const rect = dropdown.getBoundingClientRect()
    const vw = window.innerWidth
    const pad = 16

    if (rect.right > vw - pad) {
      const shift = rect.right - (vw - pad)
      dropdown.style.transform = `translateX(calc(-50% - ${shift}px))`
    } else if (rect.left < pad) {
      const shift = pad - rect.left
      dropdown.style.transform = `translateX(calc(-50% + ${shift}px))`
    }
  }

  _closeAllDropdowns() {
    this.dropdownTargets.forEach(d => {
      d.classList.remove("mn-dropdown--open")
      d.querySelectorAll(".mn-submenu--visible").forEach(s => s.classList.remove("mn-submenu--visible"))
      d.querySelectorAll(".mn-submenu-open").forEach(s => s.classList.remove("mn-submenu-open"))
    })
    this.element.querySelectorAll(".mn-nav-item--active").forEach(t => {
      t.classList.remove("mn-nav-item--active")
      t.setAttribute("aria-expanded", "false")
    })
    this.activeDropdown = null
  }

  _openMobile() {
    this.mobileOverlayTarget.classList.add("mn-mobile--open")
    this.hamburgerTarget.classList.add("mn-hamburger--open")
    this.hamburgerTarget.setAttribute("aria-expanded", "true")
    document.body.style.overflow = "hidden"
  }

  _closeMobile() {
    this.mobileOverlayTarget.classList.remove("mn-mobile--open")
    this.hamburgerTarget.classList.remove("mn-hamburger--open")
    this.hamburgerTarget.setAttribute("aria-expanded", "false")
    document.body.style.overflow = ""

    // Reset all drill-down panels
    this.mobilePanelTargets.forEach(p => p.classList.remove("mn-mpanel--active"))
    this.activeMobileDepth = 0
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      if (this.activeMobileDepth > 0) {
        const activePanel = this.mobilePanelTargets.find(p => p.classList.contains("mn-mpanel--active"))
        if (activePanel) {
          activePanel.classList.remove("mn-mpanel--active")
          this.activeMobileDepth--
        }
      } else if (this.mobileOverlayTarget.classList.contains("mn-mobile--open")) {
        this._closeMobile()
      } else {
        this._closeAllDropdowns()
      }
    }
  }

  _onResize() {
    if (!this._isMobile() && this.mobileOverlayTarget.classList.contains("mn-mobile--open")) {
      this._closeMobile()
    }
    if (this._isMobile()) {
      this._closeAllDropdowns()
    }
  }

  _clearTimeouts() {
    if (this.hoverTimeout) { clearTimeout(this.hoverTimeout); this.hoverTimeout = null }
    if (this.leaveTimeout) { clearTimeout(this.leaveTimeout); this.leaveTimeout = null }
  }

  _isMobile() {
    return window.matchMedia("(max-width: 992px)").matches
  }
}
